'use client';

import Image from 'next/image';
import React from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { FavoriteGameButton } from '@/components/FavoriteGameButton';
import { useTranslations } from '@/hooks/useTranslations';
import { useLocaleFromPath } from '@/hooks/useLocaleFromPath';
import type { GameForFrontend } from '@/lib/cms';
import type { GameProviderEntry } from '@/lib/cms';
import { getPathForLocale } from '@/lib/i18n';
import { getContentImageMeta } from '@/lib/image-meta';
import { getGameImageCandidates } from '@/lib/game-image';
import { getProviderLogoUrl } from '@/lib/provider-image';
import { SafeGameImage } from '@/components/SafeGameImage';
import { MotionReveal } from '@/components/MotionReveal';
import { MotionStagger } from '@/components/MotionStagger';
import type { GamePresetSlug } from './presets';

const GAMES_PER_PAGE = 12;

/** Map translation key to CMS type/volatility values for filtering */
const GAME_TYPE_MAP: Record<string, string[]> = {
  'games.videoSlots': ['video slot', 'video', 'slot'],
  'games.classicSlots': ['classic', 'fruit'],
  'games.jackpotGames': ['jackpot', 'megaways'],
  'games.tableGames': ['table', 'blackjack', 'roulette', 'poker', 'baccarat'],
};
const VOLATILITY_MAP: Record<string, string[]> = {
  'games.volatilityLow': ['low'],
  'games.volatilityMedium': ['medium', 'med'],
  'games.volatilityHigh': ['high'],
};

function ProviderBadge({ provider, providerSlug }: { provider: string; providerSlug: string }) {
  const [showFallback, setShowFallback] = React.useState(false);
  const badge = !providerSlug || showFallback ? (
    <span className="text-[10px] font-bold uppercase tracking-wider text-white drop-shadow-md">
      <span className="text-red-400">◆</span> {provider}
    </span>
  ) : (
    <Image
      src={getProviderLogoUrl(providerSlug)}
      alt={provider}
      title={provider}
      width={60}
      height={60}
      sizes="60px"
      className="h-[60px] w-[60px] object-contain"
      onError={() => setShowFallback(true)}
    />
  );

  if (!providerSlug) return badge;

  return (
    <LocalizedLink
      href={`/game-providers/${providerSlug}`}
      className="inline-flex items-center justify-center rounded-lg transition-opacity hover:opacity-90"
      aria-label={provider}
      title={provider}
    >
      {badge}
    </LocalizedLink>
  );
}

function GamesLoadingSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 text-slate-300">
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/15 border-t-[#ffb74d]" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function GamesGridSkeleton({
  label,
  columnsClassName,
}: {
  label: string;
  columnsClassName: string;
}) {
  return (
    <div className={`col-span-full grid grid-cols-1 gap-6 ${columnsClassName}`}>
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-2xl border border-white/5 bg-[#1e2235] animate-pulse"
        >
          <div className="h-48 bg-white/5" />
          <div className="space-y-4 p-5">
            <div className="mx-auto h-5 w-2/3 rounded bg-white/10" />
            <div className="mx-auto h-7 w-28 rounded-full bg-white/10" />
            <div className="h-11 rounded-lg bg-white/10" />
            <div className="h-10 rounded-lg bg-white/5" />
            <div className="space-y-2 border-t border-white/5 pt-4">
              <div className="h-3 rounded bg-white/10" />
              <div className="h-3 rounded bg-white/10" />
              <div className="h-3 rounded bg-white/10" />
            </div>
          </div>
        </div>
      ))}
      <div className="col-span-full flex justify-center pt-2">
        <GamesLoadingSpinner label={label} />
      </div>
    </div>
  );
}

const GAME_TYPE_KEYS = ['games.videoSlots', 'games.classicSlots', 'games.jackpotGames', 'games.tableGames'] as const;
const VOLATILITY_KEYS = ['games.volatilityLow', 'games.volatilityMedium', 'games.volatilityHigh'] as const;
const PRESET_LINKS = [
  { href: '/games/best-slots', labelKey: 'games.menu.bestSlots' },
  { href: '/games/big-slot-wins', labelKey: 'games.menu.bigSlotWins' },
  { href: '/games/crash-casino-games', labelKey: 'games.menu.crashCasinoGames' },
  { href: '/games/free-demo-slots', labelKey: 'games.menu.freeDemoSlots' },
  { href: '/games/games-of-the-year', labelKey: 'games.menu.gamesOfTheYear' },
  { href: '/games/megaways-slots', labelKey: 'games.menu.megawaysSlots' },
  { href: '/games/mobile-slots', labelKey: 'games.menu.mobileSlots' },
  { href: '/games/new-slots', labelKey: 'games.menu.newSlots' },
] as const;

function parseRtp(rtp: string): number | null {
  const m = String(rtp ?? '').match(/[\d.]+/);
  return m ? parseFloat(m[0]) : null;
}

function parseHlRank(rank: string): number | null {
  const m = String(rank ?? '').match(/[\d.]+/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

type SortOption = 'hlrank' | 'title-asc' | 'title-desc' | 'rtp-desc' | 'rtp-asc';
type Props = {
  initialGames: GameForFrontend[];
  providers: GameProviderEntry[];
  casinoAffiliateUrlsByProvider?: Record<string, string[]>;
  initialProviderSlugs?: string[];
  initialPreset?: string;
  defaultCountryFilter?: string | null;
  skipGeoCountryFilter?: boolean;
  pageTitle?: string;
  pageDescription?: string;
  breadcrumbLabel?: string;
  showSortControls?: boolean;
  showFilterControls?: boolean;
  maxResults?: number;
};

function isGamePreset(value: string): value is GamePresetSlug {
  return [
    'best-slots',
    'big-slot-wins',
    'crash-casino-games',
    'free-demo-slots',
    'games-of-the-year',
    'megaways-slots',
    'mobile-slots',
    'new-slots',
  ].includes(value);
}

function matchesPreset(game: GameForFrontend, preset: GamePresetSlug): boolean {
  const title = String(game.title ?? '').toLowerCase();
  const type = String(game.type ?? '').toLowerCase();
  const features = String(game.features ?? '').toLowerCase();
  const theme = String(game.theme ?? '').toLowerCase();
  const technology = String(game.technology ?? '').toLowerCase();
  const description = String(game.description ?? '').toLowerCase();
  const releaseDate = String(game.releaseDate ?? '').trim();
  const maxWinNum = Number(String(game.maxWin ?? '').replace(/[^\d.]/g, ''));

  if (preset === 'best-slots') return true;
  if (preset === 'big-slot-wins') return Number.isFinite(maxWinNum) && maxWinNum >= 1000;
  if (preset === 'crash-casino-games') {
    return (
      title.includes('crash') ||
      type.includes('crash') ||
      features.includes('crash') ||
      description.includes('crash')
    );
  }
  if (preset === 'free-demo-slots') return Boolean(String(game.demoUrl ?? '').trim());
  if (preset === 'games-of-the-year') {
    const year = Number(releaseDate.slice(0, 4));
    return Number.isFinite(year) && year >= new Date().getFullYear() - 1;
  }
  if (preset === 'megaways-slots') {
    return (
      title.includes('megaways') ||
      type.includes('megaways') ||
      features.includes('megaways') ||
      theme.includes('megaways')
    );
  }
  if (preset === 'mobile-slots') {
    return technology.includes('mobile') || description.includes('mobile') || theme.includes('mobile');
  }
  if (preset === 'new-slots') {
    const year = Number(releaseDate.slice(0, 4));
    return Number.isFinite(year) && year >= new Date().getFullYear() - 2;
  }
  return false;
}

function getPresetLabelKey(preset: GamePresetSlug): string {
  if (preset === 'best-slots') return 'games.menu.bestSlots';
  if (preset === 'big-slot-wins') return 'games.menu.bigSlotWins';
  if (preset === 'crash-casino-games') return 'games.menu.crashCasinoGames';
  if (preset === 'free-demo-slots') return 'games.menu.freeDemoSlots';
  if (preset === 'games-of-the-year') return 'games.menu.gamesOfTheYear';
  if (preset === 'megaways-slots') return 'games.menu.megawaysSlots';
  if (preset === 'mobile-slots') return 'games.menu.mobileSlots';
  return 'games.menu.newSlots';
}

const PAGE_SIZE = 500;

export function GamesCatalog({
  initialGames,
  providers,
  casinoAffiliateUrlsByProvider = {},
  initialProviderSlugs = [],
  initialPreset = '',
  defaultCountryFilter = null,
  skipGeoCountryFilter = false,
  pageTitle,
  pageDescription,
  breadcrumbLabel,
  showSortControls = true,
  showFilterControls = true,
  maxResults,
}: Props) {
  const { t } = useTranslations();
  const locale = useLocaleFromPath();
  const router = useRouter();
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [games, setGames] = React.useState<GameForFrontend[]>(initialGames);
  const [isLoading, setIsLoading] = React.useState(initialGames.length === 0);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(initialGames.length > 0 ? 1 : 0);
  const [pageCount, setPageCount] = React.useState(initialGames.length > 0 ? 1 : 0);
  const [totalGames, setTotalGames] = React.useState(initialGames.length);
  const [selectedProviders, setSelectedProviders] = React.useState<Set<string>>(
    () => new Set(initialProviderSlugs.filter(Boolean)),
  );
  const [selectedTypes, setSelectedTypes] = React.useState<Set<string>>(new Set());
  const [rtpMin, setRtpMin] = React.useState<string>('');
  const [rtpMax, setRtpMax] = React.useState<string>('');
  const [selectedVolatility, setSelectedVolatility] = React.useState<string | null>(null);
  const [sortBy, setSortBy] = React.useState<SortOption>('hlrank');
  const [activePreset, setActivePreset] = React.useState<GamePresetSlug | null>(
    isGamePreset(initialPreset) ? initialPreset : null
  );
  const [geoCountryFilter] = React.useState(
    () => (skipGeoCountryFilter ? null : defaultCountryFilter),
  );

  React.useEffect(() => {
    setSelectedProviders(new Set(initialProviderSlugs.filter(Boolean)));
  }, [initialProviderSlugs.join(',')]);

  React.useEffect(() => {
    setActivePreset(isGamePreset(initialPreset) ? initialPreset : null);
  }, [initialPreset]);

  const selectedProvidersKey = React.useMemo(
    () => [...selectedProviders].sort().join(','),
    [selectedProviders]
  );
  const selectedTypesKey = React.useMemo(
    () => [...selectedTypes].sort().join(','),
    [selectedTypes]
  );

  const buildApiParams = React.useCallback(
    (page: number) => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(GAMES_PER_PAGE),
        sort: sortBy,
      });
      [...selectedProviders].sort().forEach((slug) => params.append('provider', slug));
      [...selectedTypes].sort().forEach((key) => params.append('type', key));
      if (rtpMin) params.set('rtpMin', rtpMin);
      if (rtpMax) params.set('rtpMax', rtpMax);
      if (selectedVolatility) params.set('volatility', selectedVolatility);
      if (activePreset) params.set('preset', activePreset);
      if (typeof maxResults === 'number' && Number.isFinite(maxResults) && maxResults > 0) {
        params.set('maxResults', String(maxResults));
      }
      return params;
    },
    [activePreset, maxResults, rtpMax, rtpMin, selectedProviders, selectedTypes, selectedVolatility, sortBy]
  );

  const loadGames = React.useCallback(
    async (page: number, mode: 'replace' | 'append') => {
      setIsLoading(true);
      if (mode === 'replace') {
        setLoadError(null);
      }
      try {
        const res = await fetch(`/api/games?${buildApiParams(page).toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as {
          games: GameForFrontend[];
          pageCount: number;
          total: number;
        };
        setGames((prev) => (mode === 'append' ? [...prev, ...(data.games ?? [])] : data.games ?? []));
        setCurrentPage(page);
        setPageCount(data.pageCount ?? 0);
        setTotalGames(data.total ?? 0);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load games');
        if (mode === 'replace') {
          setGames([]);
          setCurrentPage(0);
          setPageCount(0);
          setTotalGames(0);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildApiParams]
  );

  React.useEffect(() => {
    loadGames(1, 'replace');
  }, [loadGames, selectedProvidersKey, selectedTypesKey, rtpMin, rtpMax, selectedVolatility, sortBy, activePreset]);

  const gamesCountByProvider = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const g of games) {
      const slug = (g.providerSlug ?? '').trim();
      if (!slug) continue;
      map.set(slug, (map.get(slug) ?? 0) + 1);
    }
    return map;
  }, [games]);

  const sortedProviders = React.useMemo(() => {
    const withCounts = providers.map((p) => {
      const slug = (p.slug ?? '').trim();
      const fromField = p.gamesCount ?? 0; // total games in DB (preferred)
      const fromGames = slug ? gamesCountByProvider.get(slug) ?? 0 : 0; // fallback from loaded list
      const count = fromField || fromGames;
      return { provider: p, count };
    });
    withCounts.sort((a, b) => b.count - a.count || String(a.provider.name ?? '').localeCompare(String(b.provider.name ?? '')));
    return withCounts.map((item) => item.provider);
  }, [providers, gamesCountByProvider]);

  const visibleGames = games;
  const hasMore = currentPage < pageCount;
  const hasActiveFilters =
    selectedProviders.size > 0 ||
    selectedTypes.size > 0 ||
    Boolean(rtpMin) ||
    Boolean(rtpMax) ||
    Boolean(selectedVolatility) ||
    Boolean(activePreset) ||
    Boolean(geoCountryFilter);
  const gridColumnsClassName = `md:grid-cols-2 ${sidebarOpen ? 'xl:grid-cols-2' : 'xl:grid-cols-3'}`;
  const showGridLoadingOverlay = isLoading && games.length > 0;

  const toggleProvider = (slug: string) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleType = (key: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetFilters = () => {
    router.push(`${pathname ?? '/games'}?allCountries=1`);
    setSelectedProviders(new Set());
    setSelectedTypes(new Set());
    setRtpMin('');
    setRtpMax('');
    setSelectedVolatility(null);
    setActivePreset(null);
  };

  const goToRandomCasino = React.useCallback(
    (providerSlug: string) => {
      const urls = casinoAffiliateUrlsByProvider[String(providerSlug ?? '').trim()] ?? [];
      if (urls.length === 0) {
        router.push(getPathForLocale(locale, '/casinos'));
        return;
      }
      const randomUrl = String(urls[Math.floor(Math.random() * urls.length)] ?? '').trim();
      if (!randomUrl) {
        router.push(getPathForLocale(locale, '/casinos'));
        return;
      }
      window.open(randomUrl, '_blank', 'noopener,noreferrer');
    },
    [casinoAffiliateUrlsByProvider, locale, router]
  );

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div className="absolute -left-32 top-80 h-[300px] w-[300px] rounded-full bg-gradient-to-br from-purple-900/40 to-transparent blur-3xl" />
        <div className="absolute -right-40 top-20 h-[460px] w-[460px] rounded-full bg-gradient-to-bl from-blue-900/40 to-transparent blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-6xl min-h-[calc(100vh-80px)] px-6">
        {showFilterControls && (
          <aside
            className={`scrollbar-hide shrink-0 overflow-y-auto overflow-x-hidden border-r border-white/5 bg-[#0b0d19]/95 backdrop-blur-xl transition-[width] duration-200 ease-out ${
              sidebarOpen ? 'w-[300px]' : 'w-0 overflow-hidden border-r-0'
            }`}
          >
          <div className="w-[300px] p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                <span className="mr-2 text-[#ffb74d]">◇</span> {t('games.filters')}
              </h2>
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-slate-400 underline hover:text-white"
              >
                {t('games.resetAll')}
              </button>
            </div>
            <div className="mb-8">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-300">{t('games.gameProviders')}</h3>
              <div className="space-y-3">
                {sortedProviders.slice(0, 12).map((p) => {
                  const slug = p.slug?.trim() ?? '';
                  const name = p.name ?? slug;
                  const isActive = selectedProviders.has(slug);
                  return (
                    <label
                      key={p.id}
                      className={`flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 transition-colors hover:bg-white/5 hover:text-white ${
                        isActive ? 'bg-[#64b5f6]/20 text-[#64b5f6]' : 'text-slate-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => slug && toggleProvider(slug)}
                        className="h-4 w-4 rounded border-white/20 bg-white/5 text-[#ffb74d]"
                      />
                      <span className="text-sm">{name}</span>
                      <span className="ml-auto text-xs text-slate-500">
                        {typeof p.gamesCount === 'number'
                          ? p.gamesCount
                          : gamesCountByProvider.get(slug ?? '') ?? '—'}
                      </span>
                    </label>
                  );
                })}
                {providers.length > 12 && (
                  <LocalizedLink href="/game-providers" className="mt-2 block text-xs text-[#64b5f6] hover:underline">
                    + {t('games.showMore')}
                  </LocalizedLink>
                )}
              </div>
            </div>
            <div className="mb-8">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-300">{t('games.gameType')}</h3>
              <div className="space-y-3">
                {GAME_TYPE_KEYS.map((key) => (
                  <label key={key} className="flex cursor-pointer items-center gap-3 hover:text-white">
                    <input
                      type="checkbox"
                      checked={selectedTypes.has(key)}
                      onChange={() => toggleType(key)}
                      className="h-4 w-4 rounded border-white/20 bg-white/5 text-[#ffb74d]"
                    />
                    <span className="text-sm text-slate-400">{t(key)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-8">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-300">{t('games.rtp')} %</h3>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  placeholder={t('games.min')}
                  value={rtpMin}
                  onChange={(e) => {
                    setRtpMin(e.target.value);
                  }}
                  className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-[#ffb74d] focus:outline-none"
                />
                <span className="text-slate-500">–</span>
                <input
                  type="number"
                  placeholder={t('games.max')}
                  value={rtpMax}
                  onChange={(e) => {
                    setRtpMax(e.target.value);
                  }}
                  className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-[#ffb74d] focus:outline-none"
                />
              </div>
            </div>
            <div className="mb-8">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-300">{t('games.volatility')}</h3>
              <div className="flex flex-wrap gap-2">
                {VOLATILITY_KEYS.map((key) => {
                  const isActive = selectedVolatility === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setSelectedVolatility((v) => (v === key ? null : key));
                      }}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? 'border-[#64b5f6]/50 bg-[#64b5f6]/20 text-[#64b5f6]'
                          : 'border-white/10 bg-white/5 text-slate-400 hover:border-[#64b5f6]/50 hover:bg-[#64b5f6]/20 hover:text-[#64b5f6]'
                      }`}
                    >
                      {t(key)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          </aside>
        )}

        {showFilterControls && (
          <button
            type="button"
            onClick={() => setSidebarOpen((o) => !o)}
            className={`absolute left-0 top-24 z-20 flex items-center rounded-r-lg border border-l-0 border-white/10 bg-[#1e2235] text-slate-400 transition-colors hover:bg-white/10 hover:text-white ${sidebarOpen ? 'h-10 w-10 justify-center' : 'h-auto min-h-20 flex-row px-1 py-1'}`}
            aria-label={sidebarOpen ? t('games.hideFilters') : t('games.showFilters')}
          >
            {!sidebarOpen && (
              <span className="inline-block origin-center rotate-90 text-sm font-medium leading-none whitespace-nowrap">
                {t('games.filters')}
              </span>
            )}
            <svg
              className={`h-5 w-5 shrink-0 transition-transform ${sidebarOpen ? '' : 'rotate-180 -ml-1.5'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div
          className={`flex-1 pb-20 ${
            showFilterControls
              ? 'p-6 pl-14 sm:pl-16 md:p-10 md:pl-14'
              : 'p-6 pl-0 md:py-10 md:pr-10 md:pl-0'
          }`}
        >
          <Breadcrumbs
            items={
              breadcrumbLabel
                ? [
                    { label: t('nav.home'), href: '/' },
                    { label: t('nav.games'), href: '/games' },
                    { label: breadcrumbLabel },
                  ]
                : [{ label: t('nav.home'), href: '/' }, { label: t('nav.games') }]
            }
          />

          <MotionReveal className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" immediate>
            <div>
              <h1 className="mb-2 text-4xl font-bold text-white">{pageTitle || t('games.title')}</h1>
              <p className="max-w-2xl text-slate-400">{pageDescription || t('games.description')}</p>
            </div>
            {showSortControls && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-300">{t('games.sortBy')}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as SortOption);
                  }}
                  className="filters-select min-w-[180px] rounded-lg border border-white/10 bg-[#15192B] px-4 py-2.5 text-sm text-slate-100 transition-colors hover:border-white/20 focus:border-[#ffb74d] focus:outline-none focus:ring-1 focus:ring-[#ffb74d]/50"
                >
                  <option value="hlrank">{t('games.sortOptions.hlRankDefault')}</option>
                  <option value="title-asc">{t('games.sortOptions.titleAZ')}</option>
                  <option value="title-desc">{t('games.sortOptions.titleZA')}</option>
                  <option value="rtp-desc">{t('games.sortOptions.rtpHighToLow')}</option>
                  <option value="rtp-asc">{t('games.sortOptions.rtpLowToHigh')}</option>
                </select>
              </div>
            )}
          </MotionReveal>

          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('games.presetPages')}:
            </span>
            {PRESET_LINKS.map((item) => (
              <LocalizedLink
                key={item.href}
                href={item.href}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200 transition-colors hover:border-[#64b5f6]/50 hover:bg-[#64b5f6]/15 hover:text-white"
              >
                {t(item.labelKey)}
              </LocalizedLink>
            ))}
          </div>

          <div className="mb-6 flex flex-wrap items-center gap-2">
            {showFilterControls &&
              [...selectedProviders].map((slug) => {
                const provider = providers.find((p) => (p.slug ?? '').trim() === slug);
                return (
                  <span
                    key={slug}
                    className="rounded-full border border-[#64b5f6]/30 bg-[#64b5f6]/10 px-3 py-1 text-xs font-medium text-[#64b5f6]"
                  >
                    {provider?.name ?? slug}
                  </span>
                );
              })}
            {showFilterControls &&
              [...selectedTypes].map((key) => (
                <span
                  key={key}
                  className="rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-3 py-1 text-xs font-medium text-[#ffb74d]"
                >
                  {t(key)}
                </span>
              ))}
            {showFilterControls && (rtpMin || rtpMax) && (
              <span className="rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-3 py-1 text-xs font-medium text-[#ffb74d]">
                RTP: {rtpMin || '—'}–{rtpMax || '—'}%
              </span>
            )}
            {showFilterControls && selectedVolatility && (
              <span className="rounded-full border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-3 py-1 text-xs font-medium text-[#ffb74d]">
                {t(selectedVolatility)}
              </span>
            )}
            {activePreset && (
              <span className="rounded-full border border-[#64b5f6]/30 bg-[#64b5f6]/10 px-3 py-1 text-xs font-medium text-[#64b5f6]">
                {t(getPresetLabelKey(activePreset))}
              </span>
            )}
            {showFilterControls && (selectedProviders.size > 0 || selectedTypes.size > 0 || rtpMin || rtpMax || selectedVolatility || activePreset) && (
              <button
                type="button"
                onClick={resetFilters}
                className="ml-2 text-xs text-slate-500 hover:text-white"
              >
                {t('games.clearAll')}
              </button>
            )}
          </div>

          <div className="relative">
          <MotionStagger
            className={`grid grid-cols-1 gap-6 ${gridColumnsClassName}`}
            staggerStep={0.05}
          >
            {loadError ? (
              <div className="col-span-full rounded-2xl border border-dashed border-red-500/30 bg-red-500/10 p-12 text-center text-red-300">
                {loadError}
              </div>
            ) : isLoading && games.length === 0 ? (
              <GamesGridSkeleton label={t('games.loading')} columnsClassName={gridColumnsClassName} />
            ) : visibleGames.length === 0 ? (
              <div className="col-span-full rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center text-slate-400">
                {hasActiveFilters ? t('games.noGamesMatchFilters') : t('games.noGamesYet')}
              </div>
            ) : (
            visibleGames.map((game) => (
              (() => {
                const imageMeta = getContentImageMeta({
                  pageTitle: t('games.title'),
                  imageNumber: Number(game.id) || 1,
                });
                const gameKey = String(game.slug ?? '').trim() || `${game.id}-${game.providerSlug}`;
                return (
              <article
                key={gameKey}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#1e2235] transition-all duration-300 hover:border-[#ffb74d]/50 hover:shadow-lg"
              >
                <div className="relative h-48 overflow-hidden bg-black">
                  <div className="absolute left-3 top-3 z-10 flex h-[60px] w-[60px] items-center justify-center">
                    <ProviderBadge provider={game.provider} providerSlug={game.providerSlug} />
                  </div>
                  <div className="absolute right-3 top-3 z-10">
                    <FavoriteGameButton gameSlug={game.slug} className="h-8 w-8 bg-black/50 backdrop-blur-sm hover:bg-black/70" />
                  </div>
                  <SafeGameImage
                    src={game.image}
                    srcCandidates={getGameImageCandidates(game.providerSlug, game.slug, 'card')}
                    alt={imageMeta.alt}
                    title={imageMeta.title}
                    imageClassName="relative z-[1] h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    placeholderTitle={game.title}
                    placeholderContainerClassName="relative z-[1] h-full w-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <LocalizedLink href={`/games/${game.slug}`} className="mb-2 text-center text-lg font-bold text-white transition-colors hover:text-[#ffb74d]">
                    {game.title}
                  </LocalizedLink>
                  <div className="mb-4 flex justify-center">
                    <span className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-1 text-xs text-slate-300">
                      <svg className="h-3.5 w-3.5 shrink-0 text-amber-400" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path d="M12 2L9 8H3l5 4-2 6 6-4 6 4-2-6 5-4h-6L12 2z" />
                      </svg>
                      <span className="font-bold text-white"><span className="text-amber-400">HL</span>Rank</span>
                      <span className="font-mono">{game.rank}</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => goToRandomCasino(game.providerSlug)}
                    className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-[#ffb74d] py-3 font-bold text-[#4a2800] shadow-lg transition-all hover:bg-amber-400"
                  >
                    {t('games.goToCasino')}
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  </button>
                  <p className="mb-3 text-center text-[10px] text-slate-500">{t('games.gambleAware')}</p>
                  <LocalizedLink href={`/games/${game.slug}`} className="mb-4 w-full rounded-lg border border-white/10 bg-[#15192B] py-2.5 text-center text-sm font-medium uppercase tracking-wide text-slate-300 transition-colors hover:bg-[#232840] hover:text-white">
                    {t('games.playDemo')}
                  </LocalizedLink>
                  <div className="mt-auto space-y-2 border-t border-white/5 pt-4">
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{t('games.rtp')}</span>
                      <span>{game.rtp}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{t('games.volatility')}</span>
                      <span>{game.volatility}</span>
                    </div>
                  </div>
                </div>
              </article>
                );
              })()
            ))
            )}
          </MotionStagger>
          {showGridLoadingOverlay ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-3xl bg-[#0b0d19]/65 backdrop-blur-sm">
              <GamesLoadingSpinner label={t('games.loading')} />
            </div>
          ) : null}
          </div>

          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => loadGames(currentPage + 1, 'append')}
                disabled={isLoading}
                className="rounded-xl border border-white/20 bg-white/5 px-8 py-4 font-medium text-white transition-colors hover:bg-white/10"
              >
                {t('games.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
